/**
 * Recently Played Component - Shows recently played songs
 */
import React, { useState, useEffect } from 'react';
import { utilityAPI } from '../services/api';
import { useAudio } from '../contexts/AudioContext';
import './RecentlyPlayed.css';

const RecentlyPlayed = ({ showInSidebar = false }) => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const { playSong } = useAudio();

  useEffect(() => {
    loadRecentlyPlayed();
  }, [timeFilter]);

  const loadRecentlyPlayed = async () => {
    try {
      setLoading(true);
      const response = await utilityAPI.getRecentlyPlayed({
        limit: 25,
        time_filter: timeFilter
      });
      setSongs(response.data.songs);
      setError(null);
    } catch (err) {
      console.error('Failed to load recently played:', err);
      setError('Failed to load recently played songs');
    } finally {
      setLoading(false);
    }
  };

  const handleSongClick = (song, index) => {
    playSong(song, songs, index);
  };

  const formatLastPlayed = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (showInSidebar) {
    return (
      <div className="recently-played-sidebar">
        <div className="recent-header">
          <h3>Recently Played</h3>
          <button
            className="btn-refresh-mini"
            onClick={loadRecentlyPlayed}
            title="Refresh"
          >
            ↻
          </button>
        </div>

        {loading ? (
          <div className="recent-loading">Loading...</div>
        ) : error ? (
          <div className="recent-error">{error}</div>
        ) : songs.length === 0 ? (
          <div className="recent-empty">
            <p>No recently played songs</p>
            <small>Start playing music to see your history</small>
          </div>
        ) : (
          <div className="recent-list">
            {songs.slice(0, 8).map((song, index) => (
              <div
                key={song.id}
                className="recent-item-mini"
                onClick={() => handleSongClick(song, index)}
              >
                <div className="recent-info">
                  <div className="recent-title">{song.title}</div>
                  <div className="recent-artist">{song.artist}</div>
                </div>
                <div className="recent-meta">
                  <span className="recent-time">
                    {formatLastPlayed(song.last_played)}
                  </span>
                  {song.play_count > 1 && (
                    <span className="recent-plays">{song.play_count}x</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="recently-played-full">
      <div className="recent-header-full">
        <h2>Recently Played</h2>
        <div className="recent-controls">
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="time-filter-select"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <button
            className="btn-refresh"
            onClick={loadRecentlyPlayed}
            title="Refresh"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M23 4v6h-6M1 20v-6h6M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="recent-state">Loading recently played songs...</div>
      ) : error ? (
        <div className="recent-state error">{error}</div>
      ) : songs.length === 0 ? (
        <div className="recent-state empty">
          <div className="empty-icon">🎵</div>
          <h3>No recently played songs</h3>
          <p>Start playing music to build your listening history</p>
        </div>
      ) : (
        <div className="recent-grid">
          {songs.map((song, index) => (
            <div
              key={song.id}
              className="recent-card"
              onClick={() => handleSongClick(song, index)}
            >
              <div className="recent-card-content">
                <div className="recent-cover">
                  <div className="play-overlay">
                    <div className="play-button">▶</div>
                  </div>
                </div>
                <div className="recent-details">
                  <div className="recent-title">{song.title}</div>
                  <div className="recent-artist">{song.artist}</div>
                  <div className="recent-meta-full">
                    <span className="recent-time">
                      {formatLastPlayed(song.last_played)}
                    </span>
                    {song.duration && (
                      <span className="recent-duration">
                        {Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')}
                      </span>
                    )}
                    {song.play_count > 1 && (
                      <span className="recent-plays">{song.play_count} plays</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentlyPlayed;